import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateJiraExportStatus } from '../redux/slices/backgroundWorker';
import { ReduxStateType } from '../redux/store';
import useJiraService from '../services/jira';

const POLLING_INTERVAL = 10000; // 10 seconds

export const useJiraExportPolling = () => {
    const dispatch = useDispatch();
    const { getSyncStatus } = useJiraService();
    const jiraExport = useSelector((state: ReduxStateType) => state.background_worker.jira_export);
    const pollingTimers = useRef<{ [projectId: string]: NodeJS.Timeout }>({});

    const pollStatus = async (projectId: string) => {
        try {
            const response = await getSyncStatus('jira', projectId);

            if (response) {
                const { sync_status, last_synced_at, message } = response;

                dispatch(updateJiraExportStatus({
                    projectId,
                    status: {
                        status: sync_status,
                        is_completed: sync_status === 'completed',
                        last_synced_at,
                        message
                    }
                }));

                // Stop polling if completed or failed
                if (sync_status === 'completed' || sync_status === 'failed' || sync_status === 'error') {
                    if (pollingTimers.current[projectId]) {
                        clearInterval(pollingTimers.current[projectId]);
                        delete pollingTimers.current[projectId];
                    }
                }
            }
        } catch (error) {
            console.error(`Error polling status for project ${projectId}:`, error);
        }
    };

    useEffect(() => {
        // Find projects that need polling
        Object.entries(jiraExport).forEach(([projectId, exportStatus]) => {
            const needsPolling = !exportStatus.is_completed && 
                                (exportStatus.status === 'loading' || exportStatus.status === 'in_progress');
            
            if (needsPolling && !pollingTimers.current[projectId]) {
                // Initial check
                pollStatus(projectId);
                // Start interval
                pollingTimers.current[projectId] = setInterval(() => pollStatus(projectId), POLLING_INTERVAL);
            } else if (!needsPolling && pollingTimers.current[projectId]) {
                // Stop if no longer needed (e.g., completed or reset)
                clearInterval(pollingTimers.current[projectId]);
                delete pollingTimers.current[projectId];
            }
        });

        // Cleanup on unmount or if dependencies change significantly
        return () => {
            // Note: We don't necessarily want to clear all on every jiraExport change,
            // but the interval logic above handles specific clears.
            // On unmount, this will run.
        };
    }, [jiraExport]);

    // Final cleanup on unmount
    useEffect(() => {
        return () => {
            Object.values(pollingTimers.current).forEach(clearInterval);
            pollingTimers.current = {};
        };
    }, []);
};
